import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Check, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useCustomChannels } from "@/hooks/api/use-custom-channels";
import { useCustomChannelSelection } from "@/hooks/use-custom-channel-selection";
import { useSelectedRulesetId } from "@/hooks/use-selected-ruleset";
import { cn } from "@/lib/utils";

export function CustomChannelSelector() {
  const { selectedChannel, setSelectedChannel } = useCustomChannelSelection();
  const selectedRulesetId = useSelectedRulesetId();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all custom channels (aggregated by channel only, no subchannels)
  const { data: channelsData, isLoading: isLoadingChannels } =
    useCustomChannels({
      limit: 1000,
      rule_set_id: selectedRulesetId ?? undefined,
    });

  // Get unique channel names
  const channels = useMemo(() => {
    const results = channelsData?.results || [];
    const uniqueChannels = Array.from(
      new Set(results.map((c) => c.custom_channel)),
    ).map((name) => ({ custom_channel: name }));
    return uniqueChannels;
  }, [channelsData]);

  // Filter channels based on search query
  const filteredChannels = useMemo(() => {
    if (!searchQuery) return channels;
    const query = searchQuery.toLowerCase();
    return channels.filter((c) =>
      c.custom_channel.toLowerCase().includes(query),
    );
  }, [channels, searchQuery]);

  const selectedChannelObj = channels.find(
    (c) => c.custom_channel === selectedChannel,
  );

  // Channel selection handler
  const handleChannelSelect = (channelName: string) => {
    setSelectedChannel(channelName);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between gap-6 min-w-[300px] whitespace-nowrap overflow-hidden bg-white dark:bg-[hsl(195_30%_12%)]"
        >
          {selectedChannelObj
            ? selectedChannelObj.custom_channel
            : "Select channel..."}
          <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command>
          <CommandInput
            placeholder="Search channels..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {isLoadingChannels && (
              <div className="py-6 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">
                  Loading channels...
                </p>
              </div>
            )}

            {!isLoadingChannels && filteredChannels.length === 0 && (
              <CommandEmpty>No channels found.</CommandEmpty>
            )}

            {!isLoadingChannels && filteredChannels.length > 0 && (
              <CommandGroup>
                {filteredChannels.map((channel) => (
                  <CommandItem
                    key={channel.custom_channel}
                    value={channel.custom_channel}
                    onSelect={() => handleChannelSelect(channel.custom_channel)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedChannel === channel.custom_channel
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {channel.custom_channel}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
